import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, HeartHandshake, Leaf, MessageCircleHeart, FileText, Lock, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import PrivacyNotice from "@/components/PrivacyNotice";
import PasswordInput from "@/components/PasswordInput";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState({ sections: {} });
  const [progress, setProgress] = useState({});
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    api.get("/plan").then((r) => setPlan(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data.items || {})).catch(() => {});
    api.get("/guide").then((r) => setGuide(r.data)).catch(() => {});
  }, []);

  const planSectionCount = 10;
  const startedSections = Object.keys(plan.sections || {}).filter((k) => {
    const v = plan.sections[k];
    return Object.values(v || {}).some((x) => x && String(x).trim().length > 0);
  }).length;
  const planPct = Math.round((startedSections / planSectionCount) * 100);

  const bereavedItems = guide ? guide.bereaved.chapters.reduce((a, c) => a + (c.checklist?.length || 0), 0) : 0;
  const bereavedDone = Object.entries(progress).filter(([k, v]) => v && k.startsWith("bereaved__")).length;
  const bereavedPct = bereavedItems ? Math.round((bereavedDone / bereavedItems) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-3">Your dashboard</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#2D332D]">Hello, {user?.name || "friend"}.</h1>
      <p className="text-[#576056] mt-3 max-w-xl">Everything is saved. Return whenever you have strength — there is no schedule here.</p>

      {!user?.is_premium && (
        <div className="mt-8 rounded-2xl bg-[#2D332D] text-white p-6 md:p-8 flex items-start gap-4 md:gap-6" data-testid="dashboard-upgrade-banner">
          <div className="w-11 h-11 rounded-full bg-[#3D453D] flex items-center justify-center text-[#D1A793] flex-shrink-0">
            <Lock size={18} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-xl md:text-2xl leading-tight">Unlock the full plan.</div>
            <p className="text-[#B5BCB1] text-sm mt-1.5 leading-relaxed">
              A one-time £29 unlocks funeral wishes, your admin system, letters to loved ones, the Plan Builder and the AI companion. Lifetime access. All bereavement content remains free.
            </p>
          </div>
          <Link to="/upgrade" data-testid="dashboard-upgrade-cta" className="btn-primary text-sm flex-shrink-0">
            Upgrade <ArrowRight size={15} />
          </Link>
        </div>
      )}

      {/* Confidentiality reassurance */}
      <PrivacyNotice variant="card" className="mt-6" />

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <Link to="/plan" data-testid="dashboard-plan-card" className="card p-8 hover:border-[#9EAD94] transition-colors group">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="w-11 h-11 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62]">
              <FileText size={20} strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-serif text-[#9EAD94]">{planPct}%</div>
          </div>
          <h3 className="font-serif text-2xl text-[#2D332D] mb-2">My Plan</h3>
          <p className="text-[#576056] mb-4 text-[15px] leading-relaxed">
            {startedSections === 0 ? "You haven't started yet — take one section at a time." : `${startedSections} of ${planSectionCount} sections started.`}
          </p>
          <div className="h-1.5 rounded-full bg-[#F3F1ED] overflow-hidden mb-4">
            <div className="h-full bg-[#9EAD94] transition-all" style={{ width: `${planPct}%` }} />
          </div>
          <span className="inline-flex items-center gap-1.5 text-[#6B7F62] text-sm group-hover:gap-2.5 transition-all">
            Continue plan <ArrowRight size={15} />
          </span>
        </Link>

        <Link to="/guide/bereaved" data-testid="dashboard-bereaved-card" className="card p-8 hover:border-[#9EAD94] transition-colors group">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="w-11 h-11 rounded-full bg-[#F5ECE7] flex items-center justify-center text-[#D1A793]">
              <HeartHandshake size={20} strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-serif text-[#D1A793]">{bereavedPct}%</div>
          </div>
          <h3 className="font-serif text-2xl text-[#2D332D] mb-2">Bereavement checklist</h3>
          <p className="text-[#576056] mb-4 text-[15px] leading-relaxed">
            {bereavedDone === 0 ? "Nothing ticked yet. It's all still there when you're ready." : `${bereavedDone} of ${bereavedItems} tasks completed.`}
          </p>
          <div className="h-1.5 rounded-full bg-[#F3F1ED] overflow-hidden mb-4">
            <div className="h-full bg-[#D1A793] transition-all" style={{ width: `${bereavedPct}%` }} />
          </div>
          <span className="inline-flex items-center gap-1.5 text-[#6B7F62] text-sm group-hover:gap-2.5 transition-all">
            Open guide <ArrowRight size={15} />
          </span>
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Link to="/guide/prepare" data-testid="dashboard-prepare-card" className="card p-6 hover:border-[#9EAD94] transition-colors group flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62] flex-shrink-0">
            <Leaf size={18} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-lg text-[#2D332D]">Preparation guide</div>
            <div className="text-sm text-[#576056]">Wills, LPAs, wishes, letters.</div>
          </div>
          <ArrowRight size={16} className="text-[#8A9488] group-hover:translate-x-1 transition-transform" />
        </Link>

        <Link to="/chat" data-testid="dashboard-chat-card" className="card p-6 hover:border-[#9EAD94] transition-colors group flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#F5ECE7] flex items-center justify-center text-[#D1A793] flex-shrink-0">
            <MessageCircleHeart size={18} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-lg text-[#2D332D]">Ask the assistant</div>
            <div className="text-sm text-[#576056]">A warm, private place for your questions.</div>
          </div>
          <ArrowRight size={16} className="text-[#8A9488] group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Trust links + Delete account */}
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        <Link
          to="/security"
          data-testid="dashboard-security-link"
          className="card p-6 hover:border-[#9EAD94] transition-colors group flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-[#E9ECE8] flex items-center justify-center text-[#6B7F62] flex-shrink-0">
            <ShieldCheck size={18} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-lg text-[#2D332D]">Trust &amp; security</div>
            <div className="text-sm text-[#576056]">How your details are kept safe.</div>
          </div>
          <ArrowRight size={16} className="text-[#8A9488] group-hover:translate-x-1 transition-transform" />
        </Link>

        <DeleteAccountCard onDeleted={async () => { await logout(); navigate("/"); }} />
      </div>
    </div>
  );
}

function DeleteAccountCard({ onDeleted }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canDelete = confirm === "DELETE" && password.length > 0 && !busy;

  const performDelete = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.delete("/auth/me", { data: { password, confirm } });
      await onDeleted();
    } catch (e) {
      setErr(formatApiError(e));
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="dashboard-delete-open"
        onClick={() => setOpen(true)}
        className="card p-6 hover:border-[#D1A793] transition-colors group flex items-center gap-4 text-left w-full"
      >
        <div className="w-10 h-10 rounded-full bg-[#F5ECE7] flex items-center justify-center text-[#D1A793] flex-shrink-0">
          <Trash2 size={18} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="font-serif text-lg text-[#2D332D]">Delete my account</div>
          <div className="text-sm text-[#576056]">Permanently remove everything — Plan, letters, progress, chats.</div>
        </div>
        <ArrowRight size={16} className="text-[#8A9488] group-hover:translate-x-1 transition-transform" />
      </button>
    );
  }

  return (
    <div className="card p-6 border-[#D1A793]/60" data-testid="dashboard-delete-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#F5ECE7] flex items-center justify-center text-[#D1A793] flex-shrink-0">
          <AlertTriangle size={18} strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-serif text-lg text-[#2D332D] leading-tight">This can't be undone.</div>
          <p className="text-sm text-[#576056] mt-1 leading-relaxed">
            Your Plan, letters, checklist progress, chat history and payment record will be permanently deleted. If you want to keep a copy, <Link to="/plan" className="underline text-[#6B7F62]">export your Plan as a PDF</Link> first.
          </p>
        </div>
      </div>

      <label className="block text-sm font-medium text-[#2D332D] mb-1.5">Confirm your password</label>
      <PasswordInput
        testId="dashboard-delete-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        autoComplete="current-password"
        className="mb-3"
      />
      <label className="block text-sm font-medium text-[#2D332D] mb-1.5">Type <span className="font-mono text-[#D1A793]">DELETE</span> to confirm</label>
      <input
        type="text"
        className="input mb-2"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder="DELETE"
        data-testid="dashboard-delete-confirm"
      />

      {err && (
        <div className="text-sm text-[#B04A3D] mt-2" data-testid="dashboard-delete-error">{err}</div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={performDelete}
          disabled={!canDelete}
          data-testid="dashboard-delete-submit"
          className="inline-flex items-center gap-2 rounded-full bg-[#B04A3D] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#8f3b31] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Trash2 size={15} /> {busy ? "Deleting…" : "Permanently delete my account"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setPassword(""); setConfirm(""); setErr(""); }}
          disabled={busy}
          data-testid="dashboard-delete-cancel"
          className="btn-secondary text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
